import { makeAutoObservable, runInAction } from 'mobx'
import { groupBy } from 'lodash'
import { type IDropdownItem } from 'shared/ui'
import {
  apiOptionsToDropdownItems,
  apiOptionToDropdownItem,
  logger,
  numberFormat,
} from 'shared/lib'
import { LINK_HELP_GET_A_BUSINESS_TAX_ID } from 'shared/constants/links'
import { SingletonRequest } from 'shared/api/lib'
import { ComplianceApi } from 'entities/Compliance/api/compliance'
import { TenDlcApi } from 'entities/TenDlc'
import {
  ComplianceServicesList,
  OrganizationBusinessProfile,
  OrganizationTollFree,
  Summary,
} from 'entities/Compliance/model'
import {
  type IEndUserType,
  type IInternationalNumberType,
  type IOptInMethodNames,
  type EnumOptInMethodTypes,
  type IResponseOptInType,
  EnumPlanTypes,
} from 'entities/Compliance/api/types/complianceType'
import { Phone } from 'entities/Phone/model/Phone'
import { ComplianceVoiceServicesStore } from 'entities/Compliance/model/ComplianceVoiceServicesStore'
import { VoiceServicesApi } from 'entities/Compliance/api/voiceServicesApi'
import type { EntitiesStore } from 'entities/EntitiesExport'
import { UKComplianceModalStore } from 'entities/Compliance/ui/UKComplianceModal/store/UKComplianceModalStore'
import { InternationalRegulatoryBundlesStore } from 'pages/settings/pages/compliance/pages/international/store/InternationalRegulatoryBundlesStore'
import { RegulatoryBundleItem } from 'pages/settings/pages/compliance/pages/international/model/RegulatoryBundleItem'

const countryMap: { [key: string]: IDropdownItem } = {
  US: {
    id: 'US',
    label: 'United States',
  },
  CA: {
    id: 'CA',
    label: 'Canada',
  },
}

const einMap: { [key: string]: IDropdownItem } = {
  US: {
    id: 'US',
    label: 'EIN',
  },
  CA: {
    id: 'CA',
    label: 'CBN',
  },
}

const twoFaVerificationStatusMap: { [key: string]: IDropdownItem } = {
  pending: {
    id: 'pending',
    label: 'Pending 2FA Email to be Sent',
  },
  action_required: {
    id: 'action_required',
    label: 'Action Required: Check Email',
  },
  approved: {
    id: 'approved',
    label: 'Approved',
  },
}

export const businessPlanTypeMap = {
  LOW_VOLUME_STANDARD: {
    id: EnumPlanTypes.LOW_VOLUME_STANDARD,
    label: 'Low volume standard',
  },
  STANDARD: {
    id: EnumPlanTypes.STANDARD,
    label: 'Standard',
  },
} satisfies Record<
  EnumPlanTypes,
  {
    id: EnumPlanTypes
    label: string
  }
>

export class ComplianceStore {
  private _tenDLCUseCases: IDropdownItem[] = []
  private _tollFreeUseCases: IDropdownItem[] = []
  private _optInTenDLCTypes: IDropdownItem[] = []
  private _optInMethodNames: IOptInMethodNames[] = []

  private _businessProfileStatesGroup: { [key: string]: Array<IDropdownItem> } = {}
  private _complianceCountries: IDropdownItem[] = Object.values(countryMap)
  private _businessProfileBusinessTypes: IDropdownItem[] = []
  private _businessProfileJobPositions: IDropdownItem[] = []
  private _businessProfileIndustries: IDropdownItem[] = []
  private _businessProfileRegionsOfOperation: IDropdownItem[] = []
  private _businessProfileBusinessRegistrationTypes: IDropdownItem[] = []
  private _businessProfileStockExchanges: IDropdownItem[] = []
  private _businessPlanTypes: IDropdownItem[] = Object.values(businessPlanTypeMap)

  private _providers: IDropdownItem[] = []

  private _tollFreeMessageVolume: IDropdownItem[] = []
  private _optInTypes: IDropdownItem[] = []
  private _optInTypesData: IResponseOptInType[] = []
  private _number_vendor_id: number | null = null
  private _aircall_number_vendor_id: number | null = null
  private _businessInformationBrandTwoFaVerificationStatus: IDropdownItem[] = Object.values(
    twoFaVerificationStatusMap
  )

  private _summary: Summary | null = null
  private _organizationBusinessProfile: OrganizationBusinessProfile | null = null
  private _complianceServicesList: ComplianceServicesList | null = null
  private _organizationTollFree: OrganizationTollFree | null = null

  private readonly _complianceVoiceServicesStore: ComplianceVoiceServicesStore
  private readonly _ukComplianceModalStore: UKComplianceModalStore

  private _unverifiedTollFreeNumbers: Map<number, Phone> = new Map()

  private _internationalRegulatoryBundlesStore = new InternationalRegulatoryBundlesStore()

  constructor(private _entities: EntitiesStore) {
    this._complianceVoiceServicesStore = new ComplianceVoiceServicesStore(this._entities)
    this._ukComplianceModalStore = new UKComplianceModalStore(this._entities)

    makeAutoObservable(this)
  }

  get complianceVoiceServicesStore() {
    return this._complianceVoiceServicesStore
  }

  get ukComplianceModalStore() {
    return this._ukComplianceModalStore
  }

  get unverifiedTollFreeNumbers() {
    return Array.from(this._unverifiedTollFreeNumbers.values())
  }

  get organizationShakenStir() {
    return this.complianceVoiceServicesStore.shakenStir
  }

  get organizationVoiceIntegrity() {
    return this.complianceVoiceServicesStore.voiceIntegrity
  }

  get organizationCnam() {
    return this.complianceVoiceServicesStore.cnamServices
  }

  get optInTypes() {
    return this._optInTypes
  }

  get optInTypesData() {
    return this._optInTypesData
  }

  get tollFreeUseCases() {
    return this._tollFreeUseCases
  }

  get tollFreeMessageVolume() {
    return this._tollFreeMessageVolume
  }

  get tenDLCUseCases() {
    return this._tenDLCUseCases
  }

  get optInTenDLCTypes() {
    return this._optInTenDLCTypes
  }

  get optInMethodNames() {
    return this._optInMethodNames
  }

  get businessProfileRegionsOfOperation() {
    return this._businessProfileRegionsOfOperation
  }

  get businessProfileBusinessTypes() {
    return this._businessProfileBusinessTypes
  }

  get businessProfileJobPositions() {
    return this._businessProfileJobPositions
  }

  get businessProfileStockExchanges() {
    return this._businessProfileStockExchanges
  }

  get businessProfileBusinessRegistrationTypes() {
    return this._businessProfileBusinessRegistrationTypes
  }

  get complianceCountries() {
    return this._complianceCountries
  }

  get businessPlanTypes() {
    return this._businessPlanTypes
  }

  get businessInformationBrandTwoFaVerificationStatus() {
    return this._businessInformationBrandTwoFaVerificationStatus
  }

  get businessProfileIndustries() {
    return this._businessProfileIndustries
  }

  get summary() {
    return this._summary
  }

  get complianceServicesList() {
    return this._complianceServicesList
  }

  get complianceServiceCampaign() {
    return this._complianceServicesList?.campaign
  }

  get isTenDlcApproved() {
    return (
      this._complianceServicesList?.brand?.status === 'approved' &&
      this.complianceServiceCampaign?.status === 'approved'
    )
  }

  get is10DLCNotRegistered() {
    return (
      this.complianceServiceCampaign?.isStatusInProgress || !this.complianceServiceCampaign?.status
    )
  }

  get organizationBusinessProfile() {
    return this._organizationBusinessProfile
  }

  get organizationTollFree() {
    return this._organizationTollFree
  }

  get isQuestionnaire() {
    return Boolean(this._summary?.organization_id)
  }

  get isQuestionnaireCompleted() {
    return this._summary?.has_tax_id !== null && this.hasNumbersType && this.hasTargetCountry
  }

  get isTaxIdBannerClosed() {
    return this._summary?.isTaxIdBannerClosed
  }

  get hasTaxID() {
    return Boolean(this._summary?.has_tax_id)
  }

  get hasShowTaxIdBanner() {
    const trial = this._entities.subscriptionStore.trialSubscriptionStore
    const { state, stateBusinessProfile10DLC, stateTollFree } = trial

    const isLocalStep0 =
      (state === 'number_local_owner' || state === 'number_local_member') &&
      stateBusinessProfile10DLC === 'step_0'

    const isTollFreeDefault =
      (state === 'number_tollfree_owner' || state === 'number_tollfree_member') &&
      stateTollFree === 'default'

    return isLocalStep0 || isTollFreeDefault
  }

  get hasNumbersType() {
    return Boolean(this._summary?.numbers_type)
  }

  get hasTargetCountry() {
    return Boolean(this._summary?.target_country)
  }

  get targetCountryUS() {
    return Boolean(this._summary?.target_country === 'US')
  }

  get targetCountryCanada() {
    return Boolean(this._summary?.target_country === 'CA')
  }

  get businessTaxIDLink() {
    return LINK_HELP_GET_A_BUSINESS_TAX_ID
  }

  get numberVendorId() {
    return this._number_vendor_id || 2
  }

  get aircallNumberVendorId() {
    return this._aircall_number_vendor_id || 3
  }

  get internationalRegulatoryBundlesStore() {
    return this._internationalRegulatoryBundlesStore
  }

  initComplianceUKModal = () => {
    this.ukComplianceModalStore.open()
  }

  loadTenDLCUseCases = async () => {
    if (this._tenDLCUseCases.length) {
      return
    }
    try {
      const response = await TenDlcApi.getTenDlcUseCases()
      runInAction(() => {
        this._tenDLCUseCases = apiOptionsToDropdownItems(
          response.data.map((item) => ({ ...item, id: item.value }))
        )
      })
    } catch (e) {
      logger.error(e)
    }
  }
  loadOptInTenDLCTypes = async () => {
    if (this._optInTenDLCTypes.length) {
      return
    }
    try {
      const response = await ComplianceApi.getOptInMethodNames()

      runInAction(() => {
        this._optInMethodNames = response.data
        this._optInTenDLCTypes = response.data.map((item) => ({
          id: item.value,
          label: item.title,
        }))
      })
    } catch (e) {
      logger.error(e)
    }
  }

  loadBusinessProfileStates = async () => {
    if (Object.values(this._businessProfileStatesGroup).length) {
      return
    }
    try {
      const response = await ComplianceApi.getBpStates()
      const group = groupBy(response.data, 'country_id')
      runInAction(() => {
        this._businessProfileStatesGroup = Object.fromEntries(
          Object.entries(group).map(([key, value]) => [
            key,
            value.map(apiOptionToDropdownItem) as IDropdownItem[],
          ])
        )
      })
    } catch (e) {
      logger.error(e)
    }
  }

  loadBusinessProfileBusinessTypes = async () => {
    if (this._businessProfileBusinessTypes.length) {
      return
    }
    try {
      const response = await ComplianceApi.getBusinessProfileBusinessTypes()
      runInAction(() => {
        this._businessProfileBusinessTypes = apiOptionsToDropdownItems(response.data)
      })
    } catch (e) {
      logger.error(e)
    }
  }

  loadBusinessProfileJobPositions = async () => {
    if (this._businessProfileJobPositions.length) {
      return
    }
    try {
      const response = await ComplianceApi.getBusinessProfileJobPositions()
      runInAction(() => {
        this._businessProfileJobPositions = apiOptionsToDropdownItems(response.data)
      })
    } catch (e) {
      logger.error(e)
    }
  }

  loadBusinessProfileIndustries = async () => {
    if (this._businessProfileIndustries.length) {
      return
    }
    try {
      const response = await ComplianceApi.getBusinessProfileIndustries()
      runInAction(() => {
        this._businessProfileIndustries = apiOptionsToDropdownItems(response.data)
      })
    } catch (e) {
      logger.error(e)
    }
  }

  loadBusinessProfileRegionsOfOperation = async () => {
    if (this._businessProfileRegionsOfOperation.length) {
      return
    }
    try {
      const response = await ComplianceApi.getBusinessProfileRegionsOfOperation()
      runInAction(() => {
        this._businessProfileRegionsOfOperation = apiOptionsToDropdownItems(response.data)
      })
    } catch (e) {
      logger.error(e)
    }
  }

  loadBusinessProfileBusinessRegistrationTypes = async () => {
    if (this._businessProfileBusinessRegistrationTypes.length) {
      return
    }
    try {
      const response = await ComplianceApi.getBusinessProfileBusinessRegistrationTypes()
      runInAction(() => {
        this._businessProfileBusinessRegistrationTypes = apiOptionsToDropdownItems(response.data)
      })
    } catch (e) {
      logger.error(e)
    }
  }
  loadGetBusinessProfileStockExchanges = async () => {
    if (this._businessProfileStockExchanges.length) {
      return
    }
    try {
      const response = await ComplianceApi.getBusinessProfileStockExchanges()
      runInAction(() => {
        this._businessProfileStockExchanges = apiOptionsToDropdownItems(response.data)
      })
    } catch (e) {
      logger.error(e)
    }
  }

  loadProviders = async () => {
    if (this._providers.length) {
      return
    }
    try {
      const response = await ComplianceApi.getProviders()
      runInAction(() => {
        this._providers = response.data.map((item) => ({
          id: item.value,
          label: item.label === 'Tcr' ? 'TCR' : item.label,
        }))
      })
    } catch (e) {
      logger.error(e)
    }
  }

  getCountryLabel = (id: string) => {
    if (id === 'usa') {
      return countryMap['US'].label
    }
    return countryMap[id]?.label || id
  }

  getEinLabel = (id: string) => {
    if (id === 'usa') {
      return einMap['US'].label
    }
    return einMap[id]?.label || id
  }

  private _normalizeCountryId = (countryId: string | null): string | null => {
    if (!countryId) return null
    return countryId === 'usa' ? 'US' : countryId
  }

  getEinLength = (countryId: string | null): number | null => {
    const id = this._normalizeCountryId(countryId)
    if (id === 'US' || id === 'CA') return 9
    return null
  }

  getEinPlaceholder = (countryId: string | null): string => {
    const id = this._normalizeCountryId(countryId)
    if (id === 'US') return 'e.g. 12-3456789'
    if (id === 'CA') return 'e.g. 123456789'
    return 'e.g. 123456789'
  }

  formatEinForDisplay = (ein: string | null, countryId: string | null): string => {
    if (!ein) return ''
    const digits = ein.replace(/[^0-9]/g, '')
    const id = this._normalizeCountryId(countryId)
    if (id === 'US' && digits.length > 2) {
      return `${digits.slice(0, 2)}-${digits.slice(2)}`
    }
    return digits
  }

  normalizeEinInput = (input: string): string => {
    return input.replace(/[^0-9]/g, '').slice(0, 9)
  }

  getEinErrorMessageText = (countryId: string | null): string => {
    const length = this.getEinLength(countryId)
    const placeholder = this.getEinPlaceholder(countryId)
    if (!length) return ''
    return `Must be a ${length}-digit number ${placeholder}`
  }

  getStates = (countryId: string | null): IDropdownItem[] => {
    if (!countryId) {
      return Object.values(this._businessProfileStatesGroup)
        .flat()
        .sort((a, b) => a.label.localeCompare(b.label))
    }

    return this._businessProfileStatesGroup[countryId] || []
  }

  getOptInMethodName = (value: EnumOptInMethodTypes) => {
    return this.optInMethodNames.find((item) => item.value === value)?.title || ''
  }

  loadTollFreeFormFields = async () => {
    if (this._tollFreeMessageVolume.length || this._tollFreeUseCases.length) {
      return
    }
    try {
      const response = await ComplianceApi.getOrganizationTollFreeFormFields()
      runInAction(() => {
        this._tollFreeMessageVolume = response.data?.monthly_values.map((item) => ({
          id: item,
          label: numberFormat({ value: item }),
        }))
        this._tollFreeUseCases = apiOptionsToDropdownItems(response.data?.use_cases)
      })
    } catch (e) {
      logger.error(e)
    }
  }
  loadOptInTypes = async () => {
    if (this._optInTypes.length) {
      return
    }
    try {
      const response = await ComplianceApi.getOptInTypes({ filter: 'toll-free' })
      runInAction(() => {
        this._optInTypes = response.data.map((item) => ({
          id: item.key,
          label: item.title,
          data: item,
        }))

        this._optInTypesData = response.data
      })
    } catch (e) {
      logger.error(e)
    }
  }

  // Summary
  private _fetchSummary = async () => {
    try {
      const { data } = await ComplianceApi.getComplianceSummary()

      const summary = new Summary(data)

      this.setSummary(summary)
    } catch (e) {
      logger.error(e)
    }
  }

  fetchSummary = new SingletonRequest(this._fetchSummary, { cache: true }).request

  setSummary = (data: Summary) => {
    this._summary = data
    this.internationalRegulatoryBundlesStore.setBannerClosedStatus(data.isBannersClosed)
  }

  closeComplianceTaxIdBannerSummary = async () => {
    try {
      const { data } = await ComplianceApi.closeComplianceTaxIdBannerSummary()

      const summary = new Summary(data)

      this.setSummary(summary)
    } catch (e) {
      logger.error(e)
    }
  }

  // OrganizationBusinessProfile
  private _fetchOrganizationBusinessProfile = async () => {
    try {
      const { data } = await ComplianceApi.getBusinessProfileData()

      const organizationBusinessProfile = new OrganizationBusinessProfile(data)

      this.setOrganizationBusinessProfile(organizationBusinessProfile)
    } catch (e) {
      logger.error(e)
    }
  }

  fetchOrganizationBusinessProfile = new SingletonRequest(this._fetchOrganizationBusinessProfile, {
    cache: true,
  }).request

  setOrganizationBusinessProfile = (data: OrganizationBusinessProfile) => {
    this._organizationBusinessProfile = data

    return this._organizationBusinessProfile
  }

  // ComplianceServicesList (10DLC)
  private _fetchComplianceServicesList = async () => {
    try {
      const { data } = await ComplianceApi.getComplianceServicesList()

      const complianceServicesList = new ComplianceServicesList(data)

      return this.setComplianceServicesList(complianceServicesList)
    } catch (e) {
      logger.error(e)
    }
  }

  fetchComplianceServicesList = new SingletonRequest(this._fetchComplianceServicesList).request

  setComplianceServicesList = (data: ComplianceServicesList) => {
    this._complianceServicesList = data

    return this._complianceServicesList
  }

  // OrganizationTollFree
  private _fetchOrganizationTollFree = async () => {
    try {
      const { data } = await ComplianceApi.getOrganizationTollFree()

      const organizationTollFree = new OrganizationTollFree(data)

      return this.setOrganizationTollFree(organizationTollFree)
    } catch (e) {
      logger.error(e)
    }
  }

  fetchOrganizationTollFree = new SingletonRequest(this._fetchOrganizationTollFree).request

  // OrganizationUK
  private _fetchOrganizationUKCompliance = async () => {
    const country = 'GB'
    const endUserType: IEndUserType = 'business'
    const numberType: IInternationalNumberType = 'mobile'

    this._internationalRegulatoryBundlesStore.setRegulatoryBundleCountry(country)

    try {
      const { data } = await ComplianceApi.getInternationalRegulatoryBundle({
        country,
        endUserType,
        numberType,
      })

      if ('data' in data && data.data) {
        runInAction(() => {
          data.data?.forEach((item) => {
            this._internationalRegulatoryBundlesStore.setItem(new RegulatoryBundleItem(item))
          })
        })
      }

      return this._internationalRegulatoryBundlesStore
    } catch (e) {
      logger.error(e)
    }
  }

  fetchOrganizationUKCompliance = new SingletonRequest(this._fetchOrganizationUKCompliance).request

  setOrganizationTollFree = (data: OrganizationTollFree) => {
    this._organizationTollFree = data

    return this._organizationTollFree
  }

  setUnverifiedTollFreeNumber = (item: Phone) => {
    this._unverifiedTollFreeNumbers.set(item.id, item)
  }

  deleteUnverifiedTollFreeNumber = (id: number) => {
    this._unverifiedTollFreeNumbers.delete(id)
  }

  updateUnverifiedTollFreeNumbers = async () => {
    this._unverifiedTollFreeNumbers.clear()

    const allNumbers = [
      ...this._entities.numbersStore.userNumbers,
      ...this._entities.numbersStore.unassignedNumbersUser,
    ]

    allNumbers.forEach((item) => {
      if (item.isTollFree && item.isStatusUnverified && !item.hasProfile) {
        this.setUnverifiedTollFreeNumber(item)
      }
    })
  }

  loadAndUpdateUnverifiedTollFreeNumbers = async () => {
    await this._entities.numbersStore.fetchUnassignedNumbers()
    await this._entities.numbersStore.fetchNumbers()

    this.updateUnverifiedTollFreeNumbers()
  }

  loadNumberVendors = async () => {
    try {
      const response = await ComplianceApi.getNumberVendors()

      runInAction(() => {
        const numberVendors = response.data

        const twilioId = numberVendors.find((vendor) => vendor.key === 'twilio')?.id || null
        const twilioVendorId =
          numberVendors.find((vendor) => vendor.key === 'twilio-integration')?.id || null
        const intelligentId =
          numberVendors.find((vendor) => vendor.key === 'inteliquent')?.id || null

        this._number_vendor_id = intelligentId || twilioId || twilioVendorId
        this._aircall_number_vendor_id =
          numberVendors.find((vendor) => vendor.key === 'aircall-api')?.id || null
      })
    } catch (e) {
      logger.error(e)
    }
  }

  loadCnamNumbers = async (id: number) => {
    try {
      const { data } = await VoiceServicesApi.getCnamNumbers(id)

      return data.map((item) => new Phone(item, this._entities))
    } catch (e) {
      logger.error(e)
    }
  }
}
